/*
 * Wishlist (Theme settings → Joli → Wishlist).
 * Saved products are kept in this browser's localStorage as a list of product handles,
 * newest first. Heart buttons carry data-joli-wishlist="<handle>"; header counters carry
 * data-joli-wishlist-count. The wishlist page (<joli-wishlist>) renders each saved product
 * with the joli-wishlist-card section through the Section Rendering API.
 */
(() => {
  const STORAGE_KEY = 'joli-wishlist';
  const MAX_ITEMS = 50;

  const read = () => {
    try {
      const list = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
      return Array.isArray(list) ? list.filter((handle) => typeof handle === 'string' && handle) : [];
    } catch (e) {
      return [];
    }
  };

  const write = (list) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_ITEMS)));
    } catch (e) {
      // Storage full or blocked (private mode): the page still updates for this visit.
    }
    sync(list);
    document.dispatchEvent(new CustomEvent('joli:wishlist:change', { detail: { list } }));
  };

  const has = (handle) => read().includes(handle);
  const add = (handle) => write([handle, ...read().filter((item) => item !== handle)]);
  const remove = (handle) => write(read().filter((item) => item !== handle));
  const toggle = (handle) => (has(handle) ? remove(handle) : add(handle));

  function sync(list = read()) {
    document.querySelectorAll('[data-joli-wishlist]').forEach((button) => {
      button.setAttribute('aria-pressed', list.includes(button.dataset.joliWishlist) ? 'true' : 'false');
    });
    document.querySelectorAll('[data-joli-wishlist-count]').forEach((counter) => {
      const bubble = counter.querySelector('[aria-hidden]');
      const label = counter.querySelector('.visually-hidden');
      if (bubble) bubble.textContent = list.length > 0 ? (list.length < 100 ? list.length : '99+') : '';
      if (label) label.textContent = list.length > 0 ? counter.dataset.label.replace('[count]', list.length) : '';
      counter.hidden = list.length === 0;
    });
  }

  window.JoliWishlist = { list: read, has, add, remove, toggle };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-joli-wishlist]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    toggle(button.dataset.joliWishlist);
  });

  // Another tab changed the list.
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) sync();
  });

  // Cards added later (filters, sorting, recommendations, quick add, theme editor).
  new MutationObserver((mutations) => {
    if (mutations.some((mutation) => [...mutation.addedNodes].some((node) => node.nodeType === 1))) sync();
  }).observe(document.documentElement, { childList: true, subtree: true });

  sync();

  class JoliWishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-joli-wishlist-grid]');
      this.empty = this.querySelector('[data-joli-wishlist-empty]');
      this.loading = this.querySelector('[data-joli-wishlist-loading]');
      this.onChange = (event) => this.removeUnsaved(event.detail.list);
      document.addEventListener('joli:wishlist:change', this.onChange);
      this.load();
    }

    disconnectedCallback() {
      document.removeEventListener('joli:wishlist:change', this.onChange);
    }

    async load() {
      const handles = read();
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const cards = await Promise.all(
        handles.map((handle) =>
          fetch(`${root}products/${encodeURIComponent(handle)}?section_id=${this.dataset.cardSection}`)
            .then((response) => (response.ok ? response.text() : null))
            .then((html) => {
              if (!html) return { handle, card: null, gone: true };
              const card = new DOMParser().parseFromString(html, 'text/html').querySelector('.grid__item');
              return { handle, card };
            })
            .catch(() => ({ handle, card: null }))
        )
      );

      // Products that no longer exist (deleted or handle changed) are dropped from the list.
      const gone = cards.filter((item) => item.gone).map((item) => item.handle);
      if (gone.length) write(read().filter((handle) => !gone.includes(handle)));

      cards.forEach(({ handle, card }) => {
        if (!card) return;
        card.dataset.joliWishlistItem = handle;
        this.grid.appendChild(card);
      });
      this.loading.hidden = true;
      this.update();
    }

    removeUnsaved(list) {
      this.grid.querySelectorAll('[data-joli-wishlist-item]').forEach((item) => {
        if (!list.includes(item.dataset.joliWishlistItem)) item.remove();
      });
      this.update();
    }

    update() {
      const count = this.grid.children.length;
      this.grid.hidden = count === 0;
      this.empty.hidden = count > 0 || !this.loading.hidden;
    }
  }

  if (!customElements.get('joli-wishlist')) customElements.define('joli-wishlist', JoliWishlistPage);
})();
